import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "@majourneys/currency";
import { NUMBERED_PINS } from "@majourneys/shared";
import { activityHrefString } from "../../activities/lib/activity-href";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { panelStyle } from "../../../shared/ui/card-frame";
import type { AiMatch } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { useMatchPreview } from "./match-preview";
import { NumberPin, setActivePin, useIsActivePin } from "./number-pin";
import { SOFT } from "./soft-tokens";
import { usePriceUnit } from "../lib/price-unit";

/** The card's frame radius; the photo inside sits at the radius less its padding. */
const CARD_RADIUS = 18;
const CARD_PAD = 5;

/**
 * A table is free: restaurants never carry a price on any surface, whatever
 * the catalogue row still says (owner rule; dev-DB restaurant prices are
 * stale). Same test the ambassador earnings use.
 */
export function isRestaurantMatch(m: Pick<AiMatch, "segment" | "categorySlug">): boolean {
  return m.segment === "restaurants" || m.categorySlug === "restaurants";
}

/**
 * Chat kit C5, a match card: a framed card with the photo, its pin number,
 * the name, where it is, one line on why, and one line of money or action.
 *
 *  - an activity shows its price and the gold "Reserve";
 *  - a restaurant says "Free to reserve" and never a price;
 *  - an explore place shows no price and no booking word, only "View place".
 *
 * No ratings: stays are the only cards that carry one, and they have their
 * own list (stays-list.tsx). The whole card is a link to the activity page,
 * which is where detail, saving and the booking flow already live. Never a
 * "pay now" surface, per the pay-on-arrival model.
 */
export function MatchCard({
  match,
  lang,
  inTrip,
  onAdd,
  index,
}: {
  match: AiMatch;
  lang: ChatLang;
  inTrip: boolean;
  onAdd: () => void;
  /**
   * Zero-based position in the answer, shown as a badge on the photo.
   *
   * Only passed when a results map is drawn: the badge exists to say "this
   * card is pin 3", so without a map it is a number attached to nothing.
   * Absent means no badge.
   */
  index?: number;
}) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const priceUnit = usePriceUnit(match);
  const preview = useMatchPreview();
  const active = useIsActivePin(index != null ? match.slug : null);
  const bookable = match.mode !== "explore";
  const restaurant = bookable && isRestaurantMatch(match);
  const title = match.title ?? match.slug;
  const href = useMemo(
    () =>
      activityHrefString({
        lang,
        citySlug: match.citySlug,
        categorySlug: match.categorySlug,
        segment: match.segment,
        slug: match.slug,
      }),
    [lang, match.citySlug, match.categorySlug, match.segment, match.slug],
  );
  const tags = [match.categoryName, match.cityName].filter(Boolean).join(" · ");
  const addLabel = inTrip
    ? t("ai_concierge.added_to_trip", { defaultValue: "Added to trip" })
    : t("ai_concierge.add_to_trip", { defaultValue: "Add to trip" });
  const numbered = index != null && index < NUMBERED_PINS;

  // Pointing at a card lifts its pin on the map. Only a card that HAS a pin
  // takes part, so a card without one never lights up a stranger's.
  const point = numbered
    ? {
        onMouseEnter: () => setActivePin(match.slug),
        onMouseLeave: () => setActivePin(null),
        onFocus: () => setActivePin(match.slug),
        onBlur: () => setActivePin(null),
      }
    : undefined;

  const frame = panelStyle(CARD_RADIUS, "#fff", "topLit");

  return (
    <div
      className="relative w-[200px] min-w-0 shrink-0 snap-start motion-safe:transition-transform motion-safe:duration-200"
      style={{
        ...frame,
        padding: CARD_PAD,
        // The lift is a transform, the chosen hairline a shadow: nothing here
        // moves the layout of the rail.
        transform: active ? "translateY(-3px)" : undefined,
        boxShadow: active ? `${frame.boxShadow}, 0 0 0 1px ${SOFT.controlEdgeOn}` : frame.boxShadow,
      }}
      data-testid={`match-card-${match.slug}`}
      data-active={active ? "true" : undefined}
      {...point}
    >
      {/* Still a link: a preview is the nicer default, but a pick is a real
          page and must keep middle-click, cmd-click and "copy link address".
          The preview only intercepts a plain left click, and only where a
          `MatchPreviewProvider` is mounted. */}
      <a
        href={href}
        onClick={(e) => {
          if (!preview) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          preview.open(match, e.currentTarget.getBoundingClientRect());
        }}
        className="group block min-w-0 rounded-[13px] outline-none focus-visible:ring-2 focus-visible:ring-[#110E39]"
        aria-label={match.cityName ? `${title}, ${match.cityName}` : title}
      >
        <div
          className="relative h-[110px] w-full overflow-hidden"
          style={{ borderRadius: CARD_RADIUS - CARD_PAD, background: "#eceaf5" }}
        >
          {match.heroImageUrl ? (
            <ImageWithFallback
              src={match.heroImageUrl}
              alt=""
              className="h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-300 group-hover:scale-[1.03]"
              sizes="200px"
            />
          ) : (
            <div className="grid h-full w-full place-items-center">
              <Icon name="ri-image-2-line" size={22} style={{ color: SOFT.body }} aria-hidden="true" />
            </div>
          )}
          {numbered ? (
            /* Start side and RTL-safe. Only the numbered pins get a badge:
               the dot a later pin wears says "here" on a map and nothing at
               all on a card. */
            <div className="absolute start-2 top-2">
              <NumberPin index={index!} lead={index === 0} active={active} />
            </div>
          ) : null}
        </div>
        <div className="px-1.5 pb-1.5 pt-2">
          <div className="line-clamp-2 break-words text-[13.5px] font-bold leading-[18px]" style={{ color: SOFT.ink }}>
            {title}
          </div>
          {tags ? (
            <div className="line-clamp-2 break-words text-[11.5px]" style={{ color: SOFT.body }}>
              {tags}
            </div>
          ) : null}
          {/* Why this place: every published row has a subtitle written as
              card copy, and an editorial answer swaps in its own line. */}
          {match.subtitle ? (
            <div className="mt-1 line-clamp-2 text-[12px] leading-[16px]" style={{ color: SOFT.ink }}>
              {match.subtitle}
            </div>
          ) : null}
          {/* The unit rides with the figure ("From 300 MAD per person"); when
              they do not fit beside the action, the action wraps under them
              rather than being cut off. */}
          <div
            className="mt-1.5 flex flex-wrap items-baseline gap-x-1 text-[12px] font-semibold"
            data-testid="match-card-money"
          >
            {restaurant ? (
              <span style={{ color: SOFT.ok }}>
                {t("ai_concierge.free_to_reserve", { defaultValue: "Free to reserve" })}
              </span>
            ) : bookable ? (
              <>
                {match.priceMad != null ? (
                  <span className="whitespace-nowrap" style={{ color: SOFT.ink }}>
                    {t("ai_concierge.place_from", {
                      defaultValue: "From {{price}}",
                      price: displayPrice(match.priceMad),
                    })}
                    {priceUnit ? ` ${priceUnit}` : null}
                    {" · "}
                  </span>
                ) : null}
                <span className="font-bold" style={{ color: SOFT.goldInk }}>
                  {t("ai_concierge.reserve", { defaultValue: "Reserve" })}
                </span>
              </>
            ) : (
              // Explore: no price claim at all ("free to visit" was true of
              // 89 of 670 places) and no booking word.
              <span className="font-bold" style={{ color: SOFT.goldInk }}>
                {t("ai_concierge.view_place", { defaultValue: "View place" })}
              </span>
            )}
          </div>
        </div>
      </a>
      {/* Add-to-trip as a photo overlay, the way the phone does it. A white
          control with one hairline; 32px, over the 24px floor. */}
      <button
        type="button"
        onClick={onAdd}
        aria-label={addLabel}
        aria-pressed={inTrip}
        title={addLabel}
        className="mj-press absolute end-[13px] top-[13px] grid h-8 w-8 place-items-center rounded-full"
        style={
          inTrip
            ? { background: "#EAF3DE", boxShadow: "0 0 0 1px #C0DD97", color: "#3B6D11" }
            : { background: SOFT.controlFill, boxShadow: `0 0 0 1px ${SOFT.controlEdge}`, color: SOFT.ink }
        }
      >
        <Icon name={inTrip ? "ri-check-line" : "ri-add-line"} size={17} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Chat kit C5: the cards on one sideways row that snaps card by card, the way
 * the phone lays them out. The row scrolls inside itself and the page never
 * does. Vertical padding, cancelled by negative margins, gives the frames and
 * the lifted card room inside the scroll box, which clips on every side.
 */
export function MatchGrid({
  matches,
  lang,
  inTrip,
  onAdd,
  numbers,
}: {
  matches: AiMatch[];
  lang: ChatLang;
  inTrip: Set<string>;
  onAdd: (id: string) => void;
  /**
   * Slug to pin number, when a results map sits beside the cards.
   *
   * A map rather than a flag because a sectioned answer hands each section a
   * SUBSET of the matches: the third card of a section is not pin 3, and
   * numbering by local position pointed every badge at the wrong pin.
   */
  numbers?: ReadonlyMap<string, number>;
}) {
  if (matches.length === 0) return null;
  return (
    <div
      className="-mx-1 -my-2 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      data-testid="match-rail"
    >
      {matches.map((m) => (
        <MatchCard
          key={m.id || m.slug}
          match={m}
          lang={lang}
          inTrip={inTrip.has(m.id)}
          onAdd={() => onAdd(m.id)}
          index={numbers?.get(m.slug)}
        />
      ))}
    </div>
  );
}
